import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast } from "react-toastify";
import { postRightworkAPI } from "../../requests/utilities/requests";
import { API_import_maintenance_assets_csv } from "../../../../constants";
import { objectToCSV, downloadCSV } from "../../../../utilities/csvExport";

const TEMPLATE_ROWS = [
  {
    Name: '48" griddle',
    Code: "",
    Area: "Line",
    Manufacturer: "Vulcan",
    Model: "948RX",
    Serial: "",
    "Install date": "2021-04-01",
    "Purchase cost": "6800",
    "Warranty expires": "2024-04-01",
    Vendor: "",
    Notes: "",
  },
];

// Upload a CSV of assets: preview every row first (problems flagged per
// row), then import. The backend refuses a file with any problem rows, so a
// retry after fixing the CSV never duplicates assets.
function AssetImportModal({ open, onClose, homeStore, storeId, storeName, onImported }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [errorCount, setErrorCount] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setRows(null);
      setErrorCount(0);
    }
  }, [open]);

  const send = async (chosenFile, dryRun) => {
    const formData = new FormData();
    formData.append("store_id", homeStore);
    formData.append("asset_store_id", storeId);
    formData.append("file", chosenFile);
    if (dryRun) formData.append("dry_run", "1");
    return postRightworkAPI(API_import_maintenance_assets_csv, formData);
  };

  const handleFile = async (e) => {
    const chosen = e.target.files?.[0];
    e.target.value = "";
    if (!chosen) return;
    setFile(chosen);
    setRows(null);
    setBusy(true);
    try {
      const res = await send(chosen, true);
      const payload = res.data?.redux?.payload || {};
      setRows(payload.rows || []);
      setErrorCount(payload.error_count || 0);
    } catch (error) {
      setFile(null);
      toast.error(error.response?.data?.system?.message || "Couldn't read that file");
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await send(file, false);
      const imported = res.data?.redux?.payload?.imported || 0;
      toast.success(`${imported} asset${imported === 1 ? "" : "s"} imported`);
      onImported?.();
      onClose();
    } catch (error) {
      const system = error.response?.data?.system;
      if (system?.data?.rows) {
        setRows(system.data.rows);
        setErrorCount(system.data.rows.filter((r) => r.error).length);
      }
      toast.error(system?.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={busy ? undefined : onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(820px, calc(100vw - 32px))",
          maxHeight: "90vh",
          bgcolor: "white",
          borderRadius: "12px",
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          outline: "none",
        }}
      >
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Import assets</h2>
            <p className="text-sm text-slate-500 mt-1">
              Into {storeName}. One row per piece of equipment; only Name is required. Codes are
              assigned when left blank.
            </p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} disabled={busy} className="text-slate-500">
            <CloseIcon />
          </button>
        </div>

        <div className="px-6 py-4 overflow-auto flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-violet-700 text-violet-700 hover:bg-violet-50 text-sm font-medium cursor-pointer">
              <UploadFileIcon fontSize="small" />
              {file ? "Choose a different file" : "Choose CSV file"}
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} disabled={busy} />
            </label>
            <button
              type="button"
              className="text-sm text-violet-700 hover:text-violet-800 underline"
              onClick={() => downloadCSV(objectToCSV(TEMPLATE_ROWS), "asset_import_template.csv")}
            >
              Download a template
            </button>
            {file && <span className="text-sm text-slate-500 truncate">{file.name}</span>}
          </div>

          {busy && !rows && <p className="mt-6 text-sm text-slate-500">Reading file...</p>}

          {rows && (
            <>
              <p className={`mt-5 text-sm ${errorCount ? "text-red-700" : "text-slate-700"}`}>
                {errorCount
                  ? `${errorCount} of ${rows.length} rows need fixing. Fix them in the file and choose it again.`
                  : `${rows.length} asset${rows.length === 1 ? "" : "s"} ready to import.`}
              </p>
              <div className="mt-3 border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-600 bg-slate-50">
                      <th className="px-3 py-2 font-semibold">Row</th>
                      <th className="px-3 py-2 font-semibold">Name</th>
                      <th className="px-3 py-2 font-semibold">Code</th>
                      <th className="px-3 py-2 font-semibold">Area</th>
                      <th className="px-3 py-2 font-semibold">Make / Model</th>
                      <th className="px-3 py-2 font-semibold">Problem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.row} className={`border-t border-slate-100 ${r.error ? "bg-red-50" : ""}`}>
                        <td className="px-3 py-2 text-slate-500 tabular-nums">{r.row}</td>
                        <td className="px-3 py-2 text-slate-900">{r.name}</td>
                        <td className="px-3 py-2 text-slate-600">{r.code || <span className="text-slate-400">Auto</span>}</td>
                        <td className="px-3 py-2 text-slate-600">{r.location}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {[r.manufacturer, r.model_number].filter(Boolean).join(" ")}
                          {r.matched?.length ? (
                            <span className="block text-xs text-slate-400">Matched to your existing spelling</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-red-700">{r.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={busy || !rows || errorCount > 0 || rows.length === 0}
            className="px-4 py-2.5 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy && rows ? "Importing..." : rows ? `Import ${rows.length} asset${rows.length === 1 ? "" : "s"}` : "Import"}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

export default AssetImportModal;
